// What a card shows before anyone acts on it: the drafted order, the goods to pick, the invoice exactly
// as it will be issued (GST split, place of supply, next number, GSTIN check, advance deducted, vehicle)
// the message exactly as it will be sent, or a quotation or an enquiry's acknowledgement as the customer will get it,
// or a drawing under review with whose it is and what LAMP read from it, or a month's close journals with their entries
// and working, as Confirm posts them.
import { Fragment } from "react";
import {
  rupees,
  type AcknowledgementPreview,
  type CreditNotePreview,
  type DrawingReviewPreview,
  type InvoicePreview,
  type JournalsPreview,
  type MessagePreview,
  type OrderLine,
  type Preview,
  type QuotationPreview,
} from "../api.js";
import { formatDate } from "../i18n/format.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";

const gstinStatus = (w: Words): Record<NonNullable<InvoicePreview["gstinCheck"]>["status"], string> => ({
  active: w.previewActive,
  cancelled: w.previewCancelled,
  suspended: w.previewSuspended,
});

/** Order lines; without prices when the person's Roles may not see customer pricing. */
function OrderLines({
  caption,
  lines,
  total,
  totalLabel,
}: {
  caption: string;
  lines: readonly OrderLine[];
  total?: number | undefined;
  /** What the total is; the order value unless given. */
  totalLabel?: string;
}) {
  const w = useWords();
  const priced = lines.every((l) => l.unitPrice !== undefined && l.amount !== undefined);
  return (
    <table>
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{w.previewItem}</th>
          <th scope="col" className="num">
            {w.previewQuantity}
          </th>
          {priced ? (
            <>
              <th scope="col" className="num">
                {w.previewUnitPrice}
              </th>
              <th scope="col" className="num">
                {w.previewAmount}
              </th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i}>
            <td>{l.item}</td>
            <td className="num">
              {l.quantity} {l.unit}
            </td>
            {priced ? (
              <>
                <td className="num">{rupees(l.unitPrice ?? 0)}</td>
                <td className="num">{rupees(l.amount ?? 0)}</td>
              </>
            ) : null}
          </tr>
        ))}
      </tbody>
      {total === undefined || !priced ? null : (
        <tfoot>
          <tr className="total">
            <th scope="row" colSpan={3}>
              {totalLabel ?? w.previewOrderValue}
            </th>
            <td className="num">{rupees(total)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

/** A quotation as the customer will get it: to whom, its lines and total, the margin for a Role that sees it, the note. */
function Quotation({ quotation }: { quotation: QuotationPreview }) {
  const w = useWords();
  return (
    <>
      <dl className="facts">
        <dt>{w.previewQuotation}</dt>
        <dd>{quotation.number}</dd>
        <dt>{w.previewCustomer}</dt>
        <dd>{quotation.customer}</dd>
        <dt>{w.previewTo}</dt>
        <dd>{quotation.to}</dd>
        <dt>{w.previewDate}</dt>
        <dd>{quotation.date}</dd>
        <dt>{w.previewValidUntil}</dt>
        <dd>{quotation.validUntil}</dd>
        {quotation.marginPct === undefined ? null : (
          <>
            <dt>{w.previewMargin}</dt>
            <dd>{w.previewMarginPct(quotation.marginPct)}</dd>
          </>
        )}
      </dl>
      <OrderLines
        caption={w.previewQuoted}
        lines={quotation.lines}
        total={quotation.total}
        totalLabel={w.previewQuotationTotal}
      />
      {quotation.coverNote ? (
        <figure>
          <figcaption>{w.previewCoverNote}</figcaption>
          <blockquote className="message">{quotation.coverNote}</blockquote>
        </figure>
      ) : null}
    </>
  );
}

/** An enquiry's acknowledgement as the customer will get it: the enquiry, and when the quotation will follow. */
function Acknowledgement({ acknowledgement }: { acknowledgement: AcknowledgementPreview }) {
  const w = useWords();
  const { language } = useLanguage();
  const by = new Date(acknowledgement.quotationBy);
  return (
    <dl className="facts">
      <dt>{w.previewEnquiry}</dt>
      <dd>{acknowledgement.enquiry}</dd>
      <dt>{w.previewCustomer}</dt>
      <dd>{acknowledgement.customer}</dd>
      <dt>{w.previewTo}</dt>
      <dd>{acknowledgement.to}</dd>
      <dt>{w.previewAskedFor}</dt>
      <dd>{acknowledgement.description}</dd>
      <dt>{w.previewQuotationBy}</dt>
      <dd>
        {Number.isNaN(by.getTime())
          ? acknowledgement.quotationBy
          : formatDate(by, language, {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZone: "Asia/Kolkata",
            })}
      </dd>
    </dl>
  );
}

/** A credit note for returned goods, every figure its own as it will be issued. */
function CreditNote({ note }: { note: CreditNotePreview }) {
  const w = useWords();
  return (
    <>
      <dl className="facts">
        <dt>{w.previewNumber}</dt>
        <dd>{note.number}</dd>
        <dt>{w.previewDate}</dt>
        <dd>{note.date}</dd>
        <dt>{w.previewCustomer}</dt>
        <dd>{note.customer}</dd>
        <dt>{w.previewAgainstInvoice}</dt>
        <dd>{note.invoice}</dd>
      </dl>
      <table>
        <thead>
          <tr>
            <th scope="col">{w.previewItem}</th>
            <th scope="col" className="num">
              {w.previewQuantity}
            </th>
            <th scope="col" className="num">
              {w.previewTaxableValue}
            </th>
            <th scope="col" className="num">
              {w.previewGst}
            </th>
          </tr>
        </thead>
        <tbody>
          {note.lines.map((l, i) => (
            <tr key={i}>
              <td>{l.item}</td>
              <td className="num">
                {l.quantity} {l.unit}
              </td>
              <td className="num">{rupees(l.amount)}</td>
              <td className="num">{rupees(l.cgst + l.sgst + l.igst)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {note.roundOff === 0 ? null : (
            <tr>
              <th scope="row" colSpan={3}>
                {w.previewRoundOff}
              </th>
              <td className="num">{rupees(note.roundOff)}</td>
            </tr>
          )}
          <tr>
            <th scope="row" colSpan={3}>
              {w.previewTotalCredit}
            </th>
            <td className="num">{rupees(note.total)}</td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}

function Invoice({ invoice }: { invoice: InvoicePreview }) {
  const w = useWords();
  const intra = invoice.supplyType === "intra_state";
  return (
    <>
      <dl className="facts">
        <dt>{w.previewNumber}</dt>
        <dd>{invoice.number}</dd>
        <dt>{w.previewDate}</dt>
        <dd>{invoice.date}</dd>
        <dt>{w.previewBillTo}</dt>
        <dd>
          {invoice.customer.name}
          {invoice.customer.gstin ? w.previewCustomerGstin(invoice.customer.gstin) : w.previewUnregistered}
          <br />
          {invoice.customer.address}
        </dd>
        {invoice.gstinCheck ? (
          <>
            <dt>{w.previewGstinCheck}</dt>
            <dd>{w.previewGstinChecked(gstinStatus(w)[invoice.gstinCheck.status], invoice.gstinCheck.checkedOn)}</dd>
          </>
        ) : null}
        <dt>{w.previewPlaceOfSupply}</dt>
        <dd>
          {invoice.placeOfSupply} ({intra ? w.previewIntraState : w.previewInterState})
        </dd>
        <dt>{w.previewFrom}</dt>
        <dd>{w.previewSupplier(invoice.supplier.name, invoice.supplier.gstin)}</dd>
        {invoice.vehicle ? (
          <>
            <dt>{w.previewVehicle}</dt>
            <dd>{invoice.vehicle}</dd>
          </>
        ) : null}
      </dl>
      <table>
        <caption className="sr-only">{w.previewInvoiceLines}</caption>
        <thead>
          <tr>
            <th scope="col">{w.previewItem}</th>
            <th scope="col">{w.previewHsn}</th>
            <th scope="col" className="num">
              {w.previewQuantity}
            </th>
            <th scope="col" className="num">
              {w.previewTaxableValue}
            </th>
            <th scope="col" className="num">
              {w.previewGst}
            </th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((l, i) => (
            <tr key={i}>
              <td>{l.description}</td>
              <td>{l.hsn}</td>
              <td className="num">
                {l.quantity} {l.unit} × {rupees(l.unitPrice)}
              </td>
              <td className="num">{rupees(l.taxableValue)}</td>
              <td className="num">
                {l.rate}%: {rupees(l.cgst + l.sgst + l.igst)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={4}>
              {w.previewTaxableValue}
            </th>
            <td className="num">{rupees(invoice.taxableValue)}</td>
          </tr>
          {intra ? (
            <>
              <tr>
                <th scope="row" colSpan={4}>
                  {w.previewCgst}
                </th>
                <td className="num">{rupees(invoice.cgst)}</td>
              </tr>
              <tr>
                <th scope="row" colSpan={4}>
                  {w.previewSgst}
                </th>
                <td className="num">{rupees(invoice.sgst)}</td>
              </tr>
            </>
          ) : (
            <tr>
              <th scope="row" colSpan={4}>
                {w.previewIgst}
              </th>
              <td className="num">{rupees(invoice.igst)}</td>
            </tr>
          )}
          {invoice.roundOff === 0 ? null : (
            <tr>
              <th scope="row" colSpan={4}>
                {w.previewRoundOff}
              </th>
              <td className="num">{rupees(invoice.roundOff)}</td>
            </tr>
          )}
          <tr className="total">
            <th scope="row" colSpan={4}>
              {w.previewTotal}
            </th>
            <td className="num">{rupees(invoice.total)}</td>
          </tr>
          {invoice.advance ? (
            <>
              <tr>
                <th scope="row" colSpan={4}>
                  {w.previewAdvance}
                </th>
                <td className="num">{rupees(-invoice.advance)}</td>
              </tr>
              <tr className="total">
                <th scope="row" colSpan={4}>
                  {w.previewBalance}
                </th>
                <td className="num">{rupees(invoice.total - invoice.advance)}</td>
              </tr>
            </>
          ) : null}
        </tfoot>
      </table>
      {(invoice.notes ?? []).map((note) => (
        <p key={note} className="small" role="note">
          {note}
        </p>
      ))}
    </>
  );
}

/** Who a message goes to and through which Channel. */
export function MessageHeader({ message }: { message: MessagePreview }) {
  const w = useWords();
  return (
    <dl className="facts">
      <dt>{w.previewTo}</dt>
      <dd>{message.to.name}</dd>
      <dt>{w.previewThrough}</dt>
      <dd>{message.channel}</dd>
    </dl>
  );
}

/** A drawing under review: its number and revision, whose it is, its file, and what LAMP read from it. */
function DrawingReview({ review }: { review: DrawingReviewPreview }) {
  const w = useWords();
  return (
    <>
      <dl className="facts">
        <dt>{w.previewDrawing}</dt>
        <dd>{review.drawing}</dd>
        <dt>{w.previewRevision}</dt>
        <dd>{review.revision}</dd>
        <dt>{w.previewCustomer}</dt>
        <dd>{review.customer ?? w.previewNoCustomer}</dd>
        {review.read.map((r) => (
          <Fragment key={r.label}>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </Fragment>
        ))}
      </dl>
      {review.file ? (
        <p>
          <a href={review.file} target="_blank" rel="noopener">
            {w.previewOpenDrawing}
          </a>
        </p>
      ) : null}
    </>
  );
}

/** A close's journals as Confirm posts them: each one's date, entries and the working it was prepared from (#1013). */
function Journals({ preview }: { preview: JournalsPreview }) {
  const w = useWords();
  return (
    <>
      {preview.journals.map((journal) => (
        <section key={journal.id} aria-label={journal.narration}>
          <dl className="facts">
            <dt>{w.previewJournal}</dt>
            <dd>{journal.narration}</dd>
            <dt>{w.previewDate}</dt>
            <dd>{journal.date}</dd>
            {journal.reversesOn ? (
              <>
                <dt>{w.previewReversesOn}</dt>
                <dd>{journal.reversesOn}</dd>
              </>
            ) : null}
          </dl>
          <table>
            <caption className="sr-only">{w.previewEntries}</caption>
            <thead>
              <tr>
                <th scope="col">{w.previewAccount}</th>
                <th scope="col" className="num">
                  {w.previewDebit}
                </th>
                <th scope="col" className="num">
                  {w.previewCredit}
                </th>
              </tr>
            </thead>
            <tbody>
              {journal.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.name}</td>
                  <td className="num">{l.side === "debit" ? rupees(l.amount) : ""}</td>
                  <td className="num">{l.side === "credit" ? rupees(l.amount) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {journal.working.length > 0 ? (
            <section aria-label={w.previewWorking}>
              <h4>{w.previewWorking}</h4>
              <ul>
                {journal.working.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </section>
      ))}
    </>
  );
}

/** A customer's standing Notes, such as "wants a test certificate with every lot", where their order is done (#510). */
function CustomerNotes({ customer, notes }: { customer: string; notes: readonly string[] | undefined }) {
  const w = useWords();
  if (!notes || notes.length === 0) return null;
  return (
    <section className="notes" aria-label={w.previewCustomerNotes(customer)}>
      <h4>{w.previewCustomerNotes(customer)}</h4>
      <ul>
        {notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </section>
  );
}

export function PreviewView({ preview }: { preview: Preview }) {
  const w = useWords();
  return (
    <div className="doc">
      {preview.kind === "message" ? (
        <>
          <MessageHeader message={preview} />
          <blockquote className="message">{preview.text}</blockquote>
        </>
      ) : preview.kind === "sales_order" ? (
        <>
          <dl className="facts">
            <dt>{w.previewOrder}</dt>
            <dd>{preview.number}</dd>
            <dt>{w.previewCustomer}</dt>
            <dd>{preview.customer}</dd>
            <dt>{w.previewDate}</dt>
            <dd>{preview.date}</dd>
            {preview.customerPo ? (
              <>
                <dt>{w.previewCustomerPo}</dt>
                <dd>{preview.customerPo}</dd>
              </>
            ) : null}
            {preview.committedDate ? (
              <>
                <dt>{w.previewDeliveryBy}</dt>
                <dd>{preview.committedDate}</dd>
              </>
            ) : null}
          </dl>
          <OrderLines caption={w.previewOrdered} lines={preview.lines} total={preview.total} />
          <CustomerNotes customer={preview.customer} notes={preview.notes} />
        </>
      ) : preview.kind === "stock_issue" ? (
        <>
          <OrderLines caption={w.previewToPick} lines={preview.lines} />
          {preview.customer ? <CustomerNotes customer={preview.customer} notes={preview.notes} /> : null}
        </>
      ) : preview.kind === "dispatch" ? (
        <CustomerNotes customer={preview.customer} notes={preview.notes} />
      ) : preview.kind === "credit_note" ? (
        <CreditNote note={preview} />
      ) : preview.kind === "quotation" ? (
        <Quotation quotation={preview} />
      ) : preview.kind === "acknowledgement" ? (
        <Acknowledgement acknowledgement={preview} />
      ) : preview.kind === "drawing_review" ? (
        <DrawingReview review={preview} />
      ) : preview.kind === "journals" ? (
        <Journals preview={preview} />
      ) : preview.kind === "bom" || preview.kind === "routing" ? null : (
        <Invoice invoice={preview} />
      )}
    </div>
  );
}
